import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement | null} */
  #lastPanelTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.menuDrawer.addEventListener('click', this.#onMenuDrawerClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.menuDrawer.removeEventListener('click', this.#onMenuDrawerClick);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Handle delegated mobile mega menu panel interactions.
   * @param {MouseEvent} event
   */
  #onMenuDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const panelTrigger = event.target.closest('[data-panel-trigger]');
    if (panelTrigger instanceof HTMLElement && this.refs.menuDrawer.contains(panelTrigger)) {
      event.preventDefault();
      this.#openPanel(panelTrigger);
      return;
    }

    const backControl = event.target.closest('[data-panel-back]');
    if (backControl instanceof HTMLElement && this.refs.menuDrawer.contains(backControl)) {
      event.preventDefault();
      this.#closeActivePanel();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#resetPanelNavigation();
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * @param {HTMLElement} trigger
   */
  #openPanel(trigger) {
    const panelId = trigger.getAttribute('data-panel-target');
    if (!panelId) return;

    const panel = this.#getPanelById(panelId);
    if (!panel) return;

    const activePanel = this.#getActivePanel();
    if (activePanel && activePanel !== panel) {
      this.#deactivatePanel(activePanel);
    }

    this.#setAllPanelTriggersExpanded(false);

    this.#setRootListHidden(true);
    this.#setPanelActive(panel, true);
    trigger.setAttribute('aria-expanded', 'true');
    this.#lastPanelTrigger = trigger;

    this.#announcePanelState(this.#getPanelLabel(panel));
    this.#focusPanel(panel);
  }

  #closeActivePanel() {
    const activePanel = this.#getActivePanel();
    if (!activePanel) {
      this.#setAllPanelTriggersExpanded(false);
      this.#setRootListHidden(false);
      this.#lastPanelTrigger = null;
      return;
    }

    const panelId = activePanel.getAttribute('data-panel-id');
    this.#deactivatePanel(activePanel);
    this.#setRootListHidden(false);
    this.#setAllPanelTriggersExpanded(false);

    const trigger = this.#getReturnTrigger(panelId);
    this.#lastPanelTrigger = null;

    this.#announcePanelState(this.#getRootListLabel());

    if (trigger) {
      requestAnimationFrame(() => {
        trigger.focus();
      });
    }
  }

  #resetPanelNavigation() {
    const activePanel = this.#getActivePanel();
    if (activePanel) {
      this.#deactivatePanel(activePanel);
    }

    this.#setRootListHidden(false);
    this.#setAllPanelTriggersExpanded(false);
    this.#lastPanelTrigger = null;
  }

  /**
   * @param {HTMLElement} panel
   */
  #deactivatePanel(panel) {
    this.#setPanelActive(panel, false);
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} isActive
   */
  #setPanelActive(panel, isActive) {
    panel.setAttribute('data-panel-active', isActive.toString());
    panel.setAttribute('aria-hidden', (!isActive).toString());

    if (isActive) {
      panel.removeAttribute('inert');
      return;
    }

    panel.setAttribute('inert', '');
  }

  /**
   * @param {boolean} isHidden
   */
  #setRootListHidden(isHidden) {
    const rootList = this.#getRootList();
    if (!rootList) return;

    rootList.setAttribute('data-panel-root-list-hidden', isHidden.toString());
    rootList.setAttribute('aria-hidden', isHidden.toString());

    if (isHidden) {
      rootList.setAttribute('inert', '');
      return;
    }

    rootList.removeAttribute('inert');
  }

  /**
   * @param {string | null} panelId
   * @returns {HTMLElement | null}
   */
  #getReturnTrigger(panelId) {
    if (this.#lastPanelTrigger?.getAttribute('data-panel-target') === panelId) {
      return this.#lastPanelTrigger;
    }

    if (!panelId) return null;

    return /** @type {HTMLElement | null} */ (
      this.refs.menuDrawer.querySelector(`[data-panel-trigger][data-panel-target="${CSS.escape(panelId)}"]`)
    );
  }

  /**
   * @param {HTMLElement} panel
   */
  #focusPanel(panel) {
    const focusTarget =
      /** @type {HTMLElement | null} */ (panel.querySelector('[data-panel-back]')) ||
      /** @type {HTMLElement | null} */ (panel.querySelector(FOCUSABLE_SELECTOR)) ||
      /** @type {HTMLElement | null} */ (panel.querySelector('[data-panel-heading]')) ||
      /** @type {HTMLElement | null} */ (panel.querySelector('[tabindex="-1"]')) ||
      panel;

    requestAnimationFrame(() => {
      focusTarget.focus();
    });
  }

  /**
   * @param {string} panelId
   * @returns {HTMLElement | null}
   */
  #getPanelById(panelId) {
    return /** @type {HTMLElement | null} */ (
      this.refs.menuDrawer.querySelector(`[data-panel-id="${CSS.escape(panelId)}"]`)
    );
  }

  /** @returns {HTMLElement | null} */
  #getActivePanel() {
    return /** @type {HTMLElement | null} */ (this.refs.menuDrawer.querySelector('[data-panel-active="true"]'));
  }

  /** @returns {HTMLElement | null} */
  #getRootList() {
    return /** @type {HTMLElement | null} */ (this.refs.menuDrawer.querySelector('[data-panel-root-list]'));
  }

  /** @returns {HTMLElement | null} */
  #getPanelAnnouncement() {
    return /** @type {HTMLElement | null} */ (this.refs.menuDrawer.querySelector('[data-panel-announcement]'));
  }

  /**
   * @param {HTMLElement} panel
   * @returns {string}
   */
  #getPanelLabel(panel) {
    const labelledBy = panel.getAttribute('aria-labelledby');
    if (!labelledBy) return '';

    return labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent?.trim() ?? '')
      .filter(Boolean)
      .join(' ');
  }

  /** @returns {string} */
  #getRootListLabel() {
    return this.querySelector('.header-mega-menu-drawer__navigation')?.getAttribute('aria-label') ?? '';
  }

  /**
   * @param {string} message
   */
  #announcePanelState(message) {
    const announcement = this.#getPanelAnnouncement();
    if (!announcement) return;

    announcement.textContent = '';

    if (!message) return;

    requestAnimationFrame(() => {
      announcement.textContent = message;
    });
  }

  /**
   * @param {boolean} expanded
   */
  #setAllPanelTriggersExpanded(expanded) {
    const triggers = this.refs.menuDrawer.querySelectorAll('[data-panel-trigger]');

    triggers.forEach((trigger) => {
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', expanded.toString());
      }
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
